import { useCallback, useEffect, useRef, useState } from 'react';
import type Animated from 'react-native-reanimated';
import {
  Flex,
  RemoteImage,
  Typography,
  IconAdd,
  ColorValues,
} from '@banhala/ui-kit';
import type { RollingBannerListProps, RollingBannerItem } from './types';
import { useLogClickBanner } from './hooks/useLogClickBanner';
import { useLogDisplayBanner } from './hooks/useLogDisplayBanner';
import {
  useGuardedScreenWidth,
  useOpenDeeplink,
  useForegroundInterval,
  useSafeTimeout,
} from '../../../../hooks';
import { BannerScrollView } from '../BannerScrollView';

const AUTO_SCROLL_INTERVAL = 5000;

/**
 * 기본 rolling banner — 무한 스크롤(앞뒤 clone)과 자동 롤링.
 * iOS HomeBannerView 의 rolling 모드와 대응.
 *
 * 가로 스크롤 메커니즘(native pagingEnabled / web snapToInterval+drag+정착감지)은
 * BannerScrollView(.tsx/.web.tsx)에 위임하고, 여기선 clone/index/autoScroll 만 담당.
 */
export const RollingBanner = ({
  entity,
  body,
  logging,
  logDisabled,
  isVisible = true,
}: RollingBannerListProps) => {
  const screenWidth = useGuardedScreenWidth('RollingBanner');
  const { openDeeplink } = useOpenDeeplink();
  const setSafeTimeout = useSafeTimeout();
  const { logClickBannerAll, logClickBannerItem } = useLogClickBanner(logging);
  const ratio = body.item.ratio;
  const bannerHeight = screenWidth / ratio;

  const scrollRef = useRef<Animated.ScrollView>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const currentIndexRef = useRef(0);
  const isDraggingRef = useRef(false);

  const itemCount = entity.length;
  const isInfinite = itemCount > 1;
  const showIndicator = itemCount > 1;

  // 무한 스크롤: [마지막, ...원본, 첫번째]
  const infiniteItems = isInfinite
    ? [entity[itemCount - 1], ...entity, entity[0]]
    : entity;

  // 무한 스크롤 시 실제 첫 아이템은 index 1
  useEffect(() => {
    if (isInfinite) {
      scrollRef.current?.scrollTo({ x: screenWidth, animated: false });
    }
  }, [isInfinite, screenWidth]);

  useLogDisplayBanner({ entity, logging, currentIndex });

  // 자동 롤링
  const autoScroll = useCallback(() => {
    if (isDraggingRef.current) return;
    const nextRealIndex = currentIndexRef.current + 1;
    const scrollTarget = (nextRealIndex + 1) * screenWidth;
    scrollRef.current?.scrollTo({ x: scrollTarget, animated: true });

    if (nextRealIndex >= itemCount) {
      // 클론(첫번째)에 도달 → 애니메이션 후 실제 첫번째로 점프
      setSafeTimeout(() => {
        scrollRef.current?.scrollTo({ x: screenWidth, animated: false });
      }, 400);
      currentIndexRef.current = 0;
      setCurrentIndex(0);
    } else {
      currentIndexRef.current = nextRealIndex;
      setCurrentIndex(nextRealIndex);
    }
  }, [itemCount, screenWidth, setSafeTimeout]);

  useForegroundInterval(
    autoScroll,
    isVisible && itemCount > 1 && !logDisabled ? AUTO_SCROLL_INTERVAL : null,
    { immediate: false },
  );

  const handleScrollBeginDrag = useCallback(() => {
    isDraggingRef.current = true;
  }, []);

  const handleScrollEndDrag = useCallback(() => {
    isDraggingRef.current = false;
  }, []);

  // 스크롤 정착 시: 무한 clone 경계 점프 + currentIndex 갱신.
  // snap=true(web)면 페이지 정렬(scrollTo)까지 수행.
  const settle = useCallback(
    (offsetX: number, snap: boolean) => {
      const scrollIndex = Math.round(offsetX / screenWidth);

      if (isInfinite) {
        if (scrollIndex === 0) {
          // 클론(마지막) → 실제 마지막으로 점프
          scrollRef.current?.scrollTo({
            x: itemCount * screenWidth,
            animated: false,
          });
          currentIndexRef.current = itemCount - 1;
          setCurrentIndex(itemCount - 1);
          return;
        }
        if (scrollIndex === itemCount + 1) {
          // 클론(첫번째) → 실제 첫번째로 점프
          scrollRef.current?.scrollTo({ x: screenWidth, animated: false });
          currentIndexRef.current = 0;
          setCurrentIndex(0);
          return;
        }
        if (snap) {
          scrollRef.current?.scrollTo({
            x: scrollIndex * screenWidth,
            animated: true,
          });
        }
        const realIndex = scrollIndex - 1;
        currentIndexRef.current = realIndex;
        setCurrentIndex(realIndex);
      } else {
        if (snap) {
          scrollRef.current?.scrollTo({
            x: scrollIndex * screenWidth,
            animated: true,
          });
        }
        currentIndexRef.current = scrollIndex;
        setCurrentIndex(scrollIndex);
      }
    },
    [screenWidth, isInfinite, itemCount],
  );

  const handleClickBannerAll = useCallback(() => {
    if (body.item.deeplink) {
      logClickBannerAll();
      openDeeplink(body.item.deeplink);
    }
  }, [openDeeplink, body.item.deeplink, logClickBannerAll]);

  const handleBannerPress = useCallback(
    (item: RollingBannerItem) => {
      if (!item.item.deeplink) return;
      logClickBannerItem(item.logging);
      openDeeplink(item.item.deeplink);
    },
    [openDeeplink, logClickBannerItem],
  );

  if (itemCount === 0) return null;

  return (
    <Flex>
      <BannerScrollView
        scrollRef={scrollRef}
        pageWidth={screenWidth}
        onSettle={settle}
        onDragStart={handleScrollBeginDrag}
        onDragEnd={handleScrollEndDrag}
      >
        {infiniteItems.map((bannerItem, index) => (
          <Flex
            key={index}
            onPress={() => handleBannerPress(bannerItem)}
            testID={`롤링배너?index=${index}`}
          >
            <RemoteImage
              uri={bannerItem.item.image_url}
              width={screenWidth}
              height={bannerHeight}
            />
          </Flex>
        ))}
      </BannerScrollView>
      {showIndicator && (
        <Flex.Row
          alignItems="center"
          position="absolute"
          bottom={16}
          right={16}
          backgroundColor="alpha_dark40"
          borderRadius={12}
          gap={2}
          padding={{
            vertical: 4,
            left: 10,
            right: body.item.deeplink ? 6 : 10,
          }}
          onPress={
            body.item.deeplink ? () => handleClickBannerAll() : undefined
          }
        >
          <Typography type="subtitle3" color="content_always_white">
            {currentIndex + 1}
          </Typography>
          <Typography type="body3" color="content_always_white">
            /
          </Typography>
          <Typography type="body3" color="content_always_white">
            {itemCount}
          </Typography>
          {body.item.deeplink && (
            <Flex margin={{ left: 4 }}>
              <IconAdd fill={ColorValues.white} width={16} height={16} />
            </Flex>
          )}
        </Flex.Row>
      )}
    </Flex>
  );
};
